import { useQueries } from "@tanstack/react-query";
import { auditLogService } from "@/features/audit-logs/services/auditLogService";
import { healthService } from "@/features/dashboard/services/healthService";
import { reportService } from "@/features/reports/services/reportService";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { queryKeys } from "@/lib/query-keys";

const RECENT_ACTIVITY_PAGE_SIZE = 5;

/**
 * The dashboard's data, and WHO IS ALLOWED TO SEE WHICH PART OF IT.
 *
 * 🔴 THE BUG THIS SHAPE EXISTS TO PREVENT. These six queries used to fire unconditionally, for
 * everyone who could open the page. Five of them are gated in the backend — four on
 * `system:view-analytics` (report.ts guards every /reports/* handler with it) and one on
 * `system:view-audit-logs` — while the ROUTE is gated on `system:view-dashboard`, which is
 * UI-scope and unlocks no data at all. So any role holding view-dashboard WITHOUT those two —
 * the EDITOR baseline, and every custom role given view-dashboard on its own — mounted the page,
 * collected five 403s, and got them rendered as a page-level error by AdminPageShell, which
 * swaps out the whole page when `error` is set. A dashboard that showed nothing but a red box.
 *
 * So a query is only ever issued by someone the backend will answer, and the permission each one
 * is keyed on here is the SAME one its handler checks. Those two lists drifting apart is what
 * produced the 403s in the first place.
 */
export function useDashboardData() {
  const { canViewAnalytics, canViewActivity } = useUserPermissions();

  const results = useQueries({
    queries: [
      {
        queryKey: queryKeys.reports.users(),
        queryFn: () => reportService.usersReport(),
        staleTime: 5 * 60 * 1000,
        enabled: canViewAnalytics,
      },
      {
        queryKey: queryKeys.reports.content(),
        queryFn: () => reportService.contentReport(),
        staleTime: 5 * 60 * 1000,
        enabled: canViewAnalytics,
      },
      {
        queryKey: queryKeys.dashboard.recentLogins(),
        queryFn: () =>
          reportService.userActivityLogins({
            page: 1,
            pageSize: RECENT_ACTIVITY_PAGE_SIZE,
          }),
        staleTime: 5 * 60 * 1000,
        enabled: canViewAnalytics,
      },
      {
        queryKey: queryKeys.auditLogs.list({
          page: 1,
          pageSize: RECENT_ACTIVITY_PAGE_SIZE,
        }),
        queryFn: () =>
          auditLogService.list({
            page: 1,
            pageSize: RECENT_ACTIVITY_PAGE_SIZE,
          }),
        staleTime: 5 * 60 * 1000,
        enabled: canViewActivity,
      },
      {
        // /api/health is public — no guard in the backend, so it needs no permission here and
        // stays the one panel every role can see.
        queryKey: queryKeys.dashboard.health(),
        queryFn: () => healthService.getHealth(),
        staleTime: 60 * 1000,
      },
    ],
  });

  // ⚠️ POSITIONAL, and it must stay in step with the array above — a name removed from one and
  // not the other does not fail to compile, it just binds to the wrong query.
  const [usersResult, contentResult, loginsResult, auditResult, healthResult] =
    results;

  return {
    usersData: usersResult.data,
    contentData: contentResult.data,
    recentLogins: loginsResult.data,
    recentAudit: auditResult.data,
    systemHealth: healthResult.data,
    // A disabled query reports isLoading false in react-query v5 (isPending && isFetching), so a
    // role that is not allowed to ask never leaves the page spinning.
    isLoading: results.some((r) => r.isLoading),
    isAnalyticsLoading:
      usersResult.isLoading ||
      contentResult.isLoading ||
      loginsResult.isLoading,
    isActivityLoading: auditResult.isLoading,
    isHealthLoading: healthResult.isLoading,
    // Only enabled queries can carry one, so this is now a REAL failure rather than a permission
    // the caller was never going to have. Keeping it page-level is deliberate: if a query the
    // user IS entitled to make still 403s, these two lists have drifted and that should be loud.
    error: (results.find((r) => r.error)?.error as Error) ?? null,
    canViewAnalytics,
    canViewActivity,
  };
}
